/**
 * Subtotale pre-accessori del preventivo, considerando i mark-up per riga.
 *
 * Logica:
 *  - Se almeno una riga ha `markupPct` valorizzato, sostituiamo la pipeline
 *    standard (markup categoria su materials/labor/finish) con un calcolo
 *    riga-per-riga: subtotal = sum(row.totalCost * (1 + override/100))
 *    per le righe con override, + sum(row.totalCost * markupBlend)
 *    per le righe senza override (markupBlend = media pesata dei markup
 *    categoria, stimata sul break-down materiali/manodopera/finitura org).
 *  - Se nessuna riga ha override, restituiamo `null` cosi' il chiamante
 *    puo' usare il subtotale standard di applyPricing senza scostamenti.
 *
 * Semplificazione: i markup categoria sono pensati per applicarsi a costi
 * SEPARATI (materiali, manodopera, finitura) ma sul singolo total_cost di
 * riga non abbiamo questo dettaglio. Per le righe senza override usiamo
 * un'approssimazione "markup medio org" = (mat + lab + fin) / 3.
 */

import type { OrgSettings } from '@/hooks/useOrgSettings';
import { PRICING_DEFAULTS } from '@/hooks/useOrgSettings';

interface RowInput {
  totalCost: number;
  markupPct?: number | null;
}

const num = (v: unknown, fallback: number): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

export function computeRowMarkupSubtotal(
  rows: RowInput[],
  settings: OrgSettings | null | undefined,
): number | null {
  if (!rows || rows.length === 0) return null;
  const hasOverride = rows.some((r) => r.markupPct != null && Number.isFinite(r.markupPct));
  if (!hasOverride) return null;

  const s = settings ?? {};
  const mMat = num(s.markup_materials_pct, PRICING_DEFAULTS.markup_materials_pct);
  const mLab = num(s.markup_labor_pct, PRICING_DEFAULTS.markup_labor_pct);
  const mFin = num(s.markup_finish_pct, PRICING_DEFAULTS.markup_finish_pct);
  // Markup medio approssimato per le righe senza override.
  // Non esatto come la pipeline a 3 componenti, ma e' la migliore stima
  // possibile senza un break-down per riga.
  const blendPct = (mMat + mLab + mFin) / 3;

  let total = 0;
  for (const r of rows) {
    const cost = num(r.totalCost, 0);
    const pct = r.markupPct != null && Number.isFinite(r.markupPct)
      ? Number(r.markupPct)
      : blendPct;
    total += cost * (1 + pct / 100);
  }
  return Math.round(total * 100) / 100;
}
